import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2 } from 'lucide-react';
import { userService } from '../../_services';

function DynamicCommission({
    titleText,
    whName,
    whId,
    whPrefix,
    whProfilePagin,
    whSearch,
    dynamicData,
    closeDynamicPopup,
    openEditProfilePopup
}) {
    const [loading, setLoading] = useState(false);
    const [btnLoader, setBtnLoader] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [binData, setBinData] = useState([]);
    const [tierDetail, setTierDetail] = useState([]);
    const [tierMilestone, setTierMilestone] = useState([]);
    const [selectedBin, setSelectedBin] = useState(null);
    
    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    // Initialize state from props
    useEffect(() => {
        if (dynamicData && dynamicData[0]?.isDynamic && dynamicData[0]?.tierJson && dynamicData[0].tierJson !== '') {
            setSelectedBin(dynamicData[0].tierJson[0]?.Bin || null);
            setTierMilestone(dynamicData[0].tierJson[0]?.Tier || []);
        }
    }, [dynamicData]);

    const handleResponse = useCallback((response) => {
        return response.text().then(text => {
            try {
                const data = text && JSON.parse(text);
                return data;
            } catch (error) {
                return [];
            }
        });
    }, []);

    const getBinTier = useCallback(async (wholesalerId) => {
        setLoading(true);
        setError('');
        
        try {
            const apidata = await userService.Bind_Tier_List(wholesalerId);
            
            if (apidata.length > 0) {
                if (apidata[0].binDetail?.length > 0 && apidata[0].tierDetail?.length > 0) {
                    setBinData(apidata[0].binDetail);
                    setTierDetail(apidata[0].tierDetail);
                    
                    if (!dynamicData || !dynamicData[0]?.isDynamic || !dynamicData[0]?.tierJson || dynamicData[0].tierJson === '') {
                        setTierMilestone(apidata[0].tierDetail);
                    }
                } else {
                    setError('Oops!, Something went wrong. Try Again.');
                }
            } else {
                setError('Oops!, Something went wrong. Try Again.');
            }
        } catch (error) {
            setError('Oops!, Something went wrong. Try Again.');
        } finally {
            setLoading(false);
        }
    }, [dynamicData]);

    useEffect(() => {
        if (whId) {
            getBinTier(parseInt(whId));
        }
    }, [whId, getBinTier]);

    const handleMilestoneChange = useCallback((index, field, value) => {
        setTierMilestone(prev => {
            const updated = [...prev];
            if (!updated[index]) {
                updated[index] = {};
            }
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    }, []);

    const validateAndSave = useCallback(async (e) => {
        e.preventDefault();
        setBtnLoader(true);
        setError('');

        try {
            // Get bin value
            const binValue = selectedBin;
            if (!binValue) {
                setError('Please select a BIN');
                setBtnLoader(false);
                return;
            }

            // Validate tier milestones
            let isValid = true;
            const wholeNumber = /^(?:0|(?:[1-9](?:\d{0,2}(?:,\d{3})+|\d*)))$/;
            
            for (let i = 0; i < tierMilestone.length; i++) {
                const tier = tierMilestone[i];
                
                if (!tier.from || !tier.to || !tier.commission) {
                    setError(`All fields are required for tier ${i + 1}`);
                    isValid = false;
                    break;
                }
                
                if (!wholeNumber.test(tier.from) || !wholeNumber.test(tier.to)) {
                    setError(`From and To fields must be whole numbers for tier ${i + 1}`);
                    isValid = false;
                    break;
                }
                
                if (!userService.validateCurrency(tier.commission)) {
                    setError(`Invalid commission value for tier ${i + 1}`);
                    isValid = false;
                    break;
                }
                
                if (parseFloat(tier.commission) > 10) {
                    setError(`Commission should not be more than 10 for tier ${i + 1}`);
                    isValid = false;
                    break;
                }
                
                if (parseInt(tier.to) <= parseInt(tier.from)) {
                    setError(`To field should be greater than From field for tier ${i + 1}`);
                    isValid = false;
                    break;
                }
            }

            if (!isValid) {
                setBtnLoader(false);
                return;
            }

            // Prepare data
            const tierJson = [{
                BIN: binValue,
                Tier: tierMilestone.map((tier, index) => ({
                    msid: index + 1,
                    tierId: tierDetail[index]?.tierId || index + 1,
                    tierName: tierDetail[index]?.tierName || `Tier ${index + 1}`,
                    from: tier.from,
                    to: tier.to,
                    commission: tier.commission
                }))
            }];

            // Save to API
            const requestOptions = {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'tierJson': JSON.stringify(tierJson)
                },
                body: JSON.stringify({
                    WholesalerId: whId
                })
            };

            const response = await fetch(`/api/admin/userCommission_Milestones`, requestOptions);
            const apidata = await handleResponse(response);

            if (apidata?.length > 0) {
                if (apidata[0].statusCode === "1") {
                    setSuccessMessage(apidata[0].statusMessage);
                    setError('');
                    setTimeout(() => {
                        handleCancel();
                    }, 2000);
                } else {
                    setError(apidata[0].statusMessage || 'Error!, Please try again.');
                    setSuccessMessage('');
                }
            } else {
                setError('Error!, Please try again.');
                setSuccessMessage('');
            }
        } catch (error) {
            setError('Error!, Please try again.');
        } finally {
            setBtnLoader(false);
        }
    }, [selectedBin, tierMilestone, tierDetail, whId, userdata, handleResponse]);

    const handleCancel = useCallback(() => {
        closeDynamicPopup(whName, whId, whPrefix, whProfilePagin, whSearch);
        openEditProfilePopup(whName, whId, whPrefix, whProfilePagin, whSearch, dynamicData?.[0]?.isDynamic);
    }, [closeDynamicPopup, openEditProfilePopup, whName, whId, whPrefix, whProfilePagin, whSearch, dynamicData]);

    return (
        <Dialog open={true} onOpenChange={handleCancel}>
            <DialogContent className="max-w-5xl">
                <DialogHeader>
                    <DialogTitle>{titleText || 'Dynamic User Commission'}</DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center items-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <>
                    <form id="dynamic-commission-form" onSubmit={validateAndSave} className="space-y-6">
                        <div className="grid gap-4">
                            <div className="grid gap-2">
                                <Label>BIN</Label>
                                <Select value={selectedBin || ''} onValueChange={setSelectedBin} disabled={btnLoader}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select BIN" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {binData.map((item, index) => (
                                            <SelectItem key={index} value={item.bin}>
                                                {item.bin}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {tierDetail.length > 0 && (
                                <div className="border rounded-lg overflow-hidden">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-[100px]">Serial No.</TableHead>
                                                <TableHead>Tier Names</TableHead>
                                                <TableHead>From</TableHead>
                                                <TableHead>To</TableHead>
                                                <TableHead>Commission</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {tierMilestone.map((tier, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>
                                                        <Input
                                                            type="text"
                                                            value={tierDetail[index]?.tierName || ''}
                                                            disabled
                                                            className="bg-muted"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input
                                                            type="number"
                                                            value={tier.from || ''}
                                                            onChange={(e) => handleMilestoneChange(index, 'from', e.target.value)}
                                                            disabled={loading || btnLoader}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input
                                                            type="number"
                                                            value={tier.to || ''}
                                                            onChange={(e) => handleMilestoneChange(index, 'to', e.target.value)}
                                                            disabled={loading || btnLoader}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            value={tier.commission || ''}
                                                            onChange={(e) => handleMilestoneChange(index, 'commission', e.target.value)}
                                                            disabled={loading || btnLoader}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </div>

                        {successMessage && (
                            <div className="text-sm text-green-700 bg-green-50 p-3 rounded-md border border-green-200">
                                {successMessage}
                            </div>
                        )}

                        {error && (
                            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                                {error}
                            </div>
                        )}

                    </form>
                        <DialogFooter>
                            <Button type="submit" form="dynamic-commission-form" disabled={loading || btnLoader}>
                                {btnLoader && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Save & Submit
                            </Button>
                            <Button type="button" variant="secondary" onClick={handleCancel} disabled={btnLoader}>
                                Cancel
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default DynamicCommission;
